import React, { createContext, useContext, useEffect } from 'react';
import {
	sendLoggedSupportQuery,
	sendNotLoggedSupportQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import type { WizardFormStep } from '@gov-nx/core/hooks';
import {
	usePoSupportDataLoad,
	useWizard,
	useFileUpload,
	MB,
} from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { useAuthStoreUser } from '@gov-nx/store';
import { useBoolean } from '@gov-nx/utils/common';
import type { PageCode } from '../definitions/codes';
import {
	FormPersonInstance,
	FormProblemInstance,
	FormRecapInstance,
	prepareSubmitData,
} from './FormDefinitions';
import type {
	FormData,
	FormPersonData,
	FormProblemData,
	SupportContextTypes,
} from './context.types';
import { useZpusobPrihlaseniOptions } from './options';

const PodporaContext = createContext<Nullable<SupportContextTypes>>(null);

interface PodporaContextProviderProps {
	children: React.ReactNode;
	code: PageCode;
	onSuccess?: () => void;
	isWeb?: boolean;
}

export function PodporaContextProvider({
	children,
	code,
	onSuccess,
	isWeb,
}: PodporaContextProviderProps) {
	const { t } = useLocale(code);
	const { toastMessageSuccess } = useMessageEvents();
	const isSubmitted = useBoolean();
	const { isLoggedIn } = useAuthStoreUser();
	const { axiosInstance } = useAxiosInstance();

	const { individualPerson, communication, ...supportData } =
		usePoSupportDataLoad({
			isEnabled: isLoggedIn,
		});

	const submitMutation = usePoMutation({
		mutationFn: async (formData: FormData) => {
			const prepared = prepareSubmitData(formData);
			if (!isLoggedIn) {
				return sendNotLoggedSupportQuery(axiosInstance, prepared);
			}
			return sendLoggedSupportQuery(axiosInstance, prepared);
		},
		onSuccess: async () => {
			toastMessageSuccess(t('formular.zprava.odeslana'));
			wizard.resetForms();
			isSubmitted.setTrue();
			onSuccess && onSuccess();
		},
	});

	const wizard = useWizard<FormData>({
		steps: [
			FormPersonInstance({ code, communication, individualPerson }),
			FormProblemInstance({ code, isLoggedIn, isWeb }),
			FormRecapInstance(),
		],
		onSubmit: async (values, onSubmitted) =>
			submitMutation.mutate(values, { onSuccess: onSubmitted }),
		initialIndexOpen: [0],
	});

	const personForm = wizard.step(
		0
	) as unknown as WizardFormStep<FormPersonData>;

	const problemForm = wizard.step(
		1
	) as unknown as WizardFormStep<FormProblemData>;

	useEffect(() => {
		if (individualPerson && communication) {
			if (individualPerson.jmeno) {
				personForm.formDefinition.formMethods.setValue(
					'jmeno',
					individualPerson.jmeno
				);
			}
			if (individualPerson.prijmeni) {
				personForm.formDefinition.formMethods.setValue(
					'prijmeni',
					individualPerson.prijmeni
				);
			}
		}
	}, [
		individualPerson,
		communication,
		wizard.resetForms,
		personForm.formDefinition.formMethods,
	]);

	const { options } = useZpusobPrihlaseniOptions();

	const fileUploadHook = useFileUpload(
		problemForm.formDefinition.formMethods,
		'priloha',
		{
			extensions: [
				'.xml',
				'.pdf',
				'.doc',
				'.docx',
				'.odt',
				'.jpg',
				'.jpeg',
				'.png',
			],
			maxFileSize: 15 * MB,
			maxSumFileSize: 15 * MB,
			maxAttachments: 10,
			multiple: true,
		}
	);

	return (
		<PodporaContext.Provider
			value={{
				code,
				controls: {
					initialLoading: isLoggedIn ? supportData.isLoading : false,
					initialError: supportData.error,

					processLoading: submitMutation.isLoading,
					processError: submitMutation.error,
				},
				individualPerson,
				isLogged: isLoggedIn,
				wizard,
				fileUploadHook,
				zpusobPrihlaseni:
					problemForm.formDefinition.formMethods.watch('zpusobPrihlaseni'),
				zpusobPrihlaseniOptions: options,
			}}>
			{children}
		</PodporaContext.Provider>
	);
}

export const usePodporaContext = (): SupportContextTypes =>
	useContext(PodporaContext) as SupportContextTypes;
